import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, FloppyDisk, Trash, Funnel, X, DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useMasterData } from "@/hooks/useMasterData";

const STATUS = ["Active", "Hold", "Resumed", "Pause", "Completed", "Cancelled"];
const PRIORITY = ["Hot", "Normal", "Urgent"];

const EMPTY = {
  q: "", status: [], priority: [], assigned_to: [],
  visa_status: [], technologies: [], skills: [], work_preferences: [],
  marketing_type: [], placement_field: [],
  sponsorship_required: null,
  experience_min: "", experience_max: "",
  deadline_from: "", deadline_to: "",
  enrollment_from: "", enrollment_to: "",
  salary_min: "", salary_max: "",
  unassigned_only: false,
  sort_by: "created_at", sort_dir: "desc",
};

function packFilter(f) {
  const out = { ...f };
  ["experience_min", "experience_max", "salary_min", "salary_max"].forEach((k) => {
    if (out[k] === "" || out[k] == null) delete out[k]; else out[k] = Number(out[k]);
  });
  ["deadline_from", "deadline_to", "enrollment_from", "enrollment_to", "q"].forEach((k) => {
    if (!out[k]) delete out[k];
  });
  Object.keys(out).forEach((k) => Array.isArray(out[k]) && out[k].length === 0 && delete out[k]);
  if (out.sponsorship_required === null) delete out.sponsorship_required;
  if (!out.unassigned_only) delete out.unassigned_only;
  return out;
}

function MultiPill({ label, options, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <select
        value=""
        onChange={(e) => {
          if (!e.target.value) return;
          if (!value.includes(e.target.value)) onChange([...value, e.target.value]);
        }}
        data-testid={testId}
        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
      >
        <option value="">— select —</option>
        {options.map((o) => {
          const v = typeof o === "string" ? o : (o.value || o.name);
          return (!value.includes(v) && <option key={v} value={v}>{v}</option>);
        })}
      </select>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {value.map((v) => (
            <span key={v} className="text-xs bg-sky-50 text-sky-700 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="text-sky-500 hover:text-red-600"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdvancedCandidateFilters({ employees = [], onResults }) {
  const [filter, setFilter] = useState(EMPTY);
  const [saved, setSaved] = useState([]);
  const [savedName, setSavedName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [total, setTotal] = useState(0);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);

  const visa = useMasterData("visa_status").items;
  const tech = useMasterData("technologies").items;
  const marketing = useMasterData("marketing_type").items;
  const placement = useMasterData("placement_field").items;
  const workPref = useMasterData("work_preference").items;

  const set = (k) => (v) => setFilter((f) => ({ ...f, [k]: v }));

  const run = useCallback(async (override) => {
    setRunning(true);
    try {
      const body = packFilter(override || filter);
      const { data } = await api.post("/candidates/search", body);
      setTotal(data.total);
      onResults?.(data.items);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setRunning(false); }
  }, [filter, onResults]);

  // Initial run + rerun on quick-filter changes. `run` is stable via useCallback deps but we
  // intentionally re-run only for quick-filter changes; suppress the lint hint.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [filter.q, filter.status, filter.priority, filter.sort_by, filter.sort_dir]);

  const loadSaved = useCallback(async () => {
    try { const { data } = await api.get("/saved-filters"); setSaved(data); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const applySaved = (f) => { setFilter({ ...EMPTY, ...f.definition }); run({ ...EMPTY, ...f.definition }); toast.success(`Applied filter "${f.name}"`); };
  const saveCurrent = async () => {
    if (!savedName.trim()) { toast.error("Give the filter a name"); return; }
    try {
      const r = await api.post("/saved-filters", { name: savedName.trim(), definition: filter, is_shared: false });
      setSaved((s) => [r.data, ...s]);
      setSavedName("");
      toast.success("Filter saved");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const deleteSaved = async (id) => {
    try { await api.delete(`/saved-filters/${id}`); setSaved((s) => s.filter((f) => f.id !== id)); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const exportRes = async (format) => {
    setExporting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates/export?format=${format}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(packFilter(filter)),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const ext = format === "excel" ? "xlsx" : format;
      a.download = `candidates_${new Date().toISOString().slice(0,10)}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (ex) { toast.error(ex.message); }
    finally { setExporting(false); }
  };

  const activeCount = useMemo(() => {
    let n = 0;
    Object.entries(filter).forEach(([k, v]) => {
      if (k === "sort_by" || k === "sort_dir" || k === "q") return;
      if (Array.isArray(v) && v.length > 0) n++;
      else if (v !== "" && v !== null && v !== false) n++;
    });
    return n;
  }, [filter]);

  return (
    <div className="space-y-3" data-testid="advanced-filters">
      {/* Quick row: search + status + priority + advanced + saved */}
      <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-md p-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={filter.q} onChange={(e) => set("q")(e.target.value)}
                 data-testid="candidate-search"
                 placeholder="Search name, email, role, employer…"
                 className="w-full border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={filter.status[0] || ""} onChange={(e) => set("status")(e.target.value ? [e.target.value] : [])}
                data-testid="quick-status" className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.priority[0] || ""} onChange={(e) => set("priority")(e.target.value ? [e.target.value] : [])}
                data-testid="quick-priority" className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All priorities</option>
          {PRIORITY.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <button type="button" onClick={() => setShowAdvanced((v) => !v)} data-testid="toggle-advanced"
                className={`text-sm px-3 py-2 rounded-md border inline-flex items-center gap-1 ${showAdvanced ? "bg-sky-600 text-white border-sky-600" : "border-slate-200 hover:bg-slate-50"}`}>
          <Funnel size={14} /> Advanced {activeCount > 0 && <span className="bg-white/20 rounded-full px-1.5 text-xs">{activeCount}</span>}
        </button>
        <div className="ml-auto text-xs text-slate-500">
          {running ? "Running…" : <><b>{total}</b> match{total === 1 ? "" : "es"}</>}
        </div>
        <button onClick={() => exportRes("excel")} disabled={exporting} data-testid="export-excel"
                className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
          <DownloadSimple size={14} /> Excel
        </button>
        <button onClick={() => exportRes("csv")} disabled={exporting} data-testid="export-csv"
                className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
          <DownloadSimple size={14} /> CSV
        </button>
      </div>

      {/* Saved filters bar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <div className="text-xs text-slate-500 mr-1">Saved filters:</div>
        {saved.length === 0 && <span className="text-xs text-slate-400">none yet</span>}
        {saved.map((f) => (
          <div key={f.id} className="text-xs inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 rounded-full pl-3 pr-1 py-0.5">
            <button onClick={() => applySaved(f)} data-testid={`saved-filter-${f.id}`}>{f.name}</button>
            <button onClick={() => deleteSaved(f.id)} className="text-slate-400 hover:text-red-600 p-1"><X size={10} /></button>
          </div>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <input placeholder="Name this filter" value={savedName} onChange={(e) => setSavedName(e.target.value)}
                 data-testid="saved-filter-name"
                 className="text-xs border border-slate-200 rounded-md px-2 py-1" />
          <button onClick={saveCurrent} data-testid="save-filter"
                  className="text-xs bg-sky-600 hover:bg-sky-700 text-white rounded-md px-3 py-1 inline-flex items-center gap-1">
            <FloppyDisk size={12} /> Save
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="advanced-panel">
          <MultiPill label="Visa" options={visa} value={filter.visa_status} onChange={set("visa_status")} testId="filter-visa" />
          <MultiPill label="Technologies" options={tech} value={filter.technologies} onChange={set("technologies")} testId="filter-tech" />
          <MultiPill label="Work Preference" options={workPref} value={filter.work_preferences} onChange={set("work_preferences")} testId="filter-workpref" />
          <MultiPill label="Marketing Type" options={marketing} value={filter.marketing_type} onChange={set("marketing_type")} testId="filter-marketing" />
          <MultiPill label="Placement Field" options={placement} value={filter.placement_field} onChange={set("placement_field")} testId="filter-placement" />
          <MultiPill label="Assigned to" options={employees.map((e) => ({ value: e.id, name: e.name }))} value={filter.assigned_to} onChange={set("assigned_to")} testId="filter-assigned" />
          <div>
            <label className="text-xs text-slate-500">Sponsorship Required</label>
            <select value={filter.sponsorship_required === null ? "" : String(filter.sponsorship_required)}
                    onChange={(e) => set("sponsorship_required")(e.target.value === "" ? null : e.target.value === "true")}
                    data-testid="filter-sponsorship"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Experience (yrs)</label>
            <div className="flex gap-1">
              <input type="number" placeholder="min" value={filter.experience_min}
                     onChange={(e) => set("experience_min")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
              <input type="number" placeholder="max" value={filter.experience_max}
                     onChange={(e) => set("experience_max")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Deadline range</label>
            <div className="flex gap-1">
              <input type="date" value={filter.deadline_from} onChange={(e) => set("deadline_from")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
              <input type="date" value={filter.deadline_to} onChange={(e) => set("deadline_to")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Enrollment range</label>
            <div className="flex gap-1">
              <input type="date" value={filter.enrollment_from} onChange={(e) => set("enrollment_from")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
              <input type="date" value={filter.enrollment_to} onChange={(e) => set("enrollment_to")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Salary range</label>
            <div className="flex gap-1">
              <input type="number" placeholder="min" value={filter.salary_min}
                     onChange={(e) => set("salary_min")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
              <input type="number" placeholder="max" value={filter.salary_max}
                     onChange={(e) => set("salary_max")(e.target.value)}
                     className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 mt-6">
            <input type="checkbox" checked={filter.unassigned_only}
                   onChange={(e) => set("unassigned_only")(e.target.checked)} />
            Show only unassigned
          </label>
          <div className="flex items-end gap-2 justify-end md:col-span-3">
            <button onClick={() => { setFilter(EMPTY); }} data-testid="filters-reset"
                    className="text-sm border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1">
              <Trash size={12} /> Reset
            </button>
            <button onClick={() => run()} data-testid="filters-apply"
                    className="text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-md px-4 py-2">
              Apply filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
